import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery, type QueryClient } from "@tanstack/react-query";

import { fetchMeOrNull } from "@/lib/auth";
import { fetchCurrencies, fetchCurrencyPresentation, keys } from "@/lib/queries";
import { sessionBrokerOf } from "@/lib/session";
import type { AuthMe, CurrencyInfo, CurrencyPresentationResponse } from "@/lib/types";
import { withBrokerOverlay } from "./brokerOverlay";
import { currencySettingsFromRegistry } from "./settings";
import { resolveCurrencyPresentation, tryResolveCurrencyPresentation } from "./resolve";
import type { CurrencyCode, CurrencyPresentation, CurrencyPresentationSettings } from "./types";

// SM-445 — operator currency configuration, fetched once and shared.
//
// Display names come from the API at runtime, never from a bundled i18n
// catalog. That is the SM-283 language-registry precedent and it is the
// only arrangement that works: an operator can rename GOLD in Backoffice
// and the change has to reach five locales without a release. The
// tradeoff is that names are not translated — they are operator copy,
// the same string in every language, which is what a currency name is.
//
// Absent configuration is a first-class state, not an error. With F1
// dark the fetcher returns [] and every currency resolves from the
// frozen seed, which is exactly what the three apps agree on — so an
// unconfigured deployment still renders identically everywhere.

//
// SM-978: a signed-in user of a broker that set a GOLD image (or under an
// ancestor that did) sees that image on GOLD — an overlay on the registry
// settings, read once per session per 5 minutes and only for a session
// that carries a broker (lib/session.ts sessionBrokerOf). The registry
// settings without it stay available (usePlatformCurrencySettings), so a
// broker image that fails to load falls back to the platform's GOLD
// image, then the bundled coin (brokerOverlay.ts, CurrencyMark).

const CurrencySettingsContext = createContext<CurrencyPresentationSettings>({});
const PlatformCurrencySettingsContext = createContext<CurrencyPresentationSettings>({});

export function CurrencySettingsProvider({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: keys.currencies(),
    queryFn: fetchCurrencies,
    // The registry changes when an operator edits it — minutes, not
    // seconds. A long stale time keeps every money figure on the page
    // from re-resolving on every navigation.
    staleTime: 5 * 60_000,
  });
  const me = useQuery({ queryKey: keys.me(), queryFn: fetchMeOrNull, staleTime: 5_000 });
  const broker = sessionBrokerOf(me.data);
  const overlay = useQuery({
    queryKey: keys.currencyPresentation(broker?.userId ?? "", broker?.brokerId ?? ""),
    queryFn: fetchCurrencyPresentation,
    enabled: broker != null,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const brokerOverlay = broker ? overlay.data : undefined;
  const platform = useMemo(() => currencySettingsFromRegistry(q.data), [q.data]);
  const settings = useMemo(() => withBrokerOverlay(platform, brokerOverlay), [platform, brokerOverlay]);
  return (
    <PlatformCurrencySettingsContext.Provider value={platform}>
      <CurrencySettingsContext.Provider value={settings}>{children}</CurrencySettingsContext.Provider>
    </PlatformCurrencySettingsContext.Provider>
  );
}

/** The operator settings to hand to `<CurrencyAmount settings={…}>`. */
export function useCurrencySettings(): CurrencyPresentationSettings {
  return useContext(CurrencySettingsContext);
}

/**
 * The registry settings WITHOUT the session broker's GOLD image (SM-978):
 * what a broken broker image falls back to.
 */
export function usePlatformCurrencySettings(): CurrencyPresentationSettings {
  return useContext(PlatformCurrencySettingsContext);
}

/**
 * The same settings, read straight from the query cache — for the code that
 * formats money OUTSIDE a render and so cannot use the context: the WS toast
 * path builds its text inside a socket callback, where there is no component
 * to hold a hook.
 *
 * Reading rather than fetching is deliberate. This is a display refinement on
 * a toast that is already on screen; an un-warmed cache resolves from the
 * frozen seed, which is the same answer every app gives an unconfigured
 * deployment. In practice the provider has fetched it at app mount.
 */
export function currencySettingsFromCache(qc: QueryClient): CurrencyPresentationSettings {
  const platform = currencySettingsFromRegistry(qc.getQueryData<CurrencyInfo[]>(keys.currencies()));
  // SM-978: the session broker's GOLD image too, from the same cache entry
  // the provider reads.
  const broker = sessionBrokerOf(qc.getQueryData<AuthMe | null>(keys.me()));
  if (!broker) return platform;
  return withBrokerOverlay(
    platform,
    qc.getQueryData<CurrencyPresentationResponse>(keys.currencyPresentation(broker.userId, broker.brokerId)),
  );
}

/**
 * A currency's fully-resolved presentation, operator config applied.
 * Throws on a code outside the fixed set — same discipline as the
 * contract's own resolver, because drawing an unknown code as GOLD
 * would put a wrong unit beside a real balance.
 */
export function useCurrencyPresentation(code: CurrencyCode): CurrencyPresentation {
  const settings = useCurrencySettings();
  return useMemo(() => resolveCurrencyPresentation(code, settings), [code, settings]);
}

/** Non-throwing variant for an unvalidated wire code. */
export function useMaybeCurrencyPresentation(code: string): CurrencyPresentation | null {
  const settings = useCurrencySettings();
  return useMemo(() => tryResolveCurrencyPresentation(code, settings), [code, settings]);
}
